import type { RecordSmokeProofRequest } from "./api";

export const NOCTANT_DOGFOOD_APP_LOOP_PROOF_KIND =
  "noctant_dogfood_app_loop_proof";
export const NOCTANT_HISTORICAL_REPLAY_LAUNCH_PROOF_KIND =
  "noctant_historical_replay_launch_proof";
export const NOCTANT_HISTORICAL_BACKTEST_LAUNCH_PROOF_KIND =
  "noctant_historical_backtest_launch_proof";

export type SmokeProofKind =
  | typeof NOCTANT_DOGFOOD_APP_LOOP_PROOF_KIND
  | typeof NOCTANT_HISTORICAL_REPLAY_LAUNCH_PROOF_KIND
  | typeof NOCTANT_HISTORICAL_BACKTEST_LAUNCH_PROOF_KIND;

const UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const NIL_UUID = "00000000-0000-0000-0000-000000000000";
const LOWER_HEX_COMMIT_PATTERN = /^[0-9a-f]{40}$/;
const LOWER_HEX_HEAD_PATTERN = /^[0-9a-f]{7,40}$/;
const RFC3339_TIMEZONE_PATTERN = /(?:Z|[+-]\d{2}:\d{2})$/;
const RUNNER_PATTERN = /^[a-z0-9_-]{1,64}$/;

export interface SmokeProofFormState {
  proof_kind: string;
  status: string;
  runner: string;
  ran_at: string;
  source_git_commit: string;
  source_git_head: string;
  account_id: string;
  launch_session_id: string;
  webhook_event_id: string;
  webhook_delivery_row_id: string;
  replay_request_id: string;
  historical_product_mode: string;
  historical_session_id: string;
  backtest_run_id: string;
}

export type SmokeProofFormField = keyof SmokeProofFormState;

export function initialSmokeProofForm(): SmokeProofFormState {
  return {
    proof_kind: NOCTANT_DOGFOOD_APP_LOOP_PROOF_KIND,
    status: "passed",
    runner: "dogfood_app_loop",
    ran_at: "",
    source_git_commit: "",
    source_git_head: "",
    account_id: "",
    launch_session_id: "",
    webhook_event_id: "",
    webhook_delivery_row_id: "",
    replay_request_id: "",
    historical_product_mode: "",
    historical_session_id: "",
    backtest_run_id: "",
  };
}

export function normalizeSmokeProofRecord(
  operatorId: string,
  orgId: string,
  form: SmokeProofFormState,
): RecordSmokeProofRequest {
  const sourceGitCommit = requiredTrimmed(
    "source_git_commit",
    form.source_git_commit,
  );
  const sourceGitHead = requiredTrimmed("source_git_head", form.source_git_head);
  validateGitCommit(sourceGitCommit);
  validateGitHead(sourceGitCommit, sourceGitHead);

  const proofKind = normalizeProofKind(form.proof_kind);
  const base = {
    operator_id: normalizeUuid("operator_id", operatorId),
    org_id: normalizeUuid("org_id", orgId),
    proof_kind: proofKind,
    status: normalizeStatus(form.status),
    runner: normalizeRunner(form.runner),
    ran_at: normalizeRanAt(form.ran_at),
    source_git_commit: sourceGitCommit,
    source_git_head: sourceGitHead,
    launch_session_id: normalizeUuid(
      "launch_session_id",
      form.launch_session_id,
    ),
  };

  if (proofKind === NOCTANT_DOGFOOD_APP_LOOP_PROOF_KIND) {
    return {
      ...base,
      account_id: normalizeUuid("account_id", form.account_id),
      webhook_event_id: normalizeUuid(
        "webhook_event_id",
        form.webhook_event_id,
      ),
      webhook_delivery_row_id: optionalUuid(
        "webhook_delivery_row_id",
        form.webhook_delivery_row_id,
      ),
      replay_request_id: optionalUuid(
        "replay_request_id",
        form.replay_request_id,
      ),
      historical_product_mode: requireBlankHistoricalMode(
        form.historical_product_mode,
      ),
      historical_session_id: requireBlankUuid(
        "historical_session_id",
        form.historical_session_id,
      ),
      backtest_run_id: requireBlankUuid("backtest_run_id", form.backtest_run_id),
    };
  }

  const expectedMode =
    proofKind === NOCTANT_HISTORICAL_REPLAY_LAUNCH_PROOF_KIND
      ? "playback"
      : "backtest";
  const historicalProductMode = normalizeHistoricalProductMode(
    form.historical_product_mode,
  );
  if (historicalProductMode !== expectedMode) {
    throw new Error(
      `historical_product_mode must be ${expectedMode} for this proof kind`,
    );
  }
  requireBlankUuid("account_id", form.account_id);

  const webhookEventId = optionalUuid("webhook_event_id", form.webhook_event_id);
  const webhookDeliveryRowId = optionalUuid(
    "webhook_delivery_row_id",
    form.webhook_delivery_row_id,
  );
  if (webhookDeliveryRowId && !webhookEventId) {
    throw new Error("webhook_delivery_row_id requires webhook_event_id");
  }
  // A playback launch opens Chart Replay and creates no session.
  if (expectedMode === "playback") {
    if (
      form.historical_session_id.trim() ||
      webhookEventId ||
      webhookDeliveryRowId ||
      form.replay_request_id.trim() ||
      form.backtest_run_id.trim()
    ) {
      throw new Error(
        "historical replay proof must not include a historical session, backtest webhook or run evidence",
      );
    }
  }

  return {
    ...base,
    account_id: null,
    webhook_event_id: webhookEventId,
    webhook_delivery_row_id: webhookDeliveryRowId,
    replay_request_id: optionalUuid("replay_request_id", form.replay_request_id),
    historical_product_mode: historicalProductMode,
    historical_session_id: optionalUuid(
      "historical_session_id",
      form.historical_session_id,
    ),
    backtest_run_id: optionalUuid("backtest_run_id", form.backtest_run_id),
  };
}

function normalizeProofKind(value: string): SmokeProofKind {
  if (
    value === NOCTANT_DOGFOOD_APP_LOOP_PROOF_KIND ||
    value === NOCTANT_HISTORICAL_REPLAY_LAUNCH_PROOF_KIND ||
    value === NOCTANT_HISTORICAL_BACKTEST_LAUNCH_PROOF_KIND
  ) {
    return value;
  }
  throw new Error("proof_kind is not a supported smoke proof kind");
}

function normalizeStatus(value: string): "passed" | "failed" {
  const status = value.trim();
  if (status !== "passed" && status !== "failed") {
    throw new Error("status must be passed or failed");
  }
  return status;
}

function normalizeRunner(value: string): string {
  const runner = requiredTrimmed("runner", value);
  if (!RUNNER_PATTERN.test(runner)) {
    throw new Error(
      "runner must contain only lowercase letters, digits, '_' or '-'",
    );
  }
  return runner;
}

function normalizeRanAt(value: string): string {
  const ranAt = requiredTrimmed("ran_at", value);
  if (!RFC3339_TIMEZONE_PATTERN.test(ranAt)) {
    throw new Error("ran_at must include an explicit RFC3339 timezone");
  }
  const millis = Date.parse(ranAt);
  if (!Number.isFinite(millis)) {
    throw new Error("ran_at must be a valid RFC3339 timestamp");
  }
  return new Date(millis).toISOString();
}

function validateGitCommit(value: string) {
  if (!LOWER_HEX_COMMIT_PATTERN.test(value)) {
    throw new Error(
      "source_git_commit must be a 40-character lowercase hex commit",
    );
  }
}

function validateGitHead(commit: string, head: string) {
  if (!LOWER_HEX_HEAD_PATTERN.test(head)) {
    throw new Error(
      "source_git_head must be a 7..=40 character lowercase hex prefix",
    );
  }
  if (!commit.startsWith(head)) {
    throw new Error("source_git_head must be a prefix of source_git_commit");
  }
}

function optionalUuid(field: string, value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : normalizeUuid(field, trimmed);
}

function requireBlankUuid(field: string, value: string): null {
  if (value.trim() !== "") {
    throw new Error(`${field} must be blank for this proof kind`);
  }
  return null;
}

function normalizeHistoricalProductMode(value: string): "playback" | "backtest" {
  const mode = value.trim();
  if (mode !== "playback" && mode !== "backtest") {
    throw new Error("historical_product_mode must be playback or backtest");
  }
  return mode;
}

function requireBlankHistoricalMode(value: string): null {
  if (value.trim() !== "") {
    throw new Error(
      "historical_product_mode must be blank for live app-loop proof",
    );
  }
  return null;
}

function normalizeUuid(field: string, value: string): string {
  const trimmed = requiredTrimmed(field, value).toLowerCase();
  if (!UUID_PATTERN.test(trimmed)) {
    throw new Error(`${field} must be a UUID`);
  }
  if (trimmed === NIL_UUID) {
    throw new Error(`${field} must not be the nil UUID`);
  }
  return trimmed;
}

function requiredTrimmed(field: string, value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") {
    throw new Error(`${field} is required`);
  }
  return trimmed;
}
